import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {navigateToStudyOverviewTab, refreshStudyFolder} from 'studies/FileUtils';
import {ExecutionPlan} from 'studies/progress/Types';
import {
  CreationTrayCallbacks,
  OnUnknownErrorAction,
  showCreationTrayTask,
} from 'studies/sidepanel/CreationTrayTask';
import {ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE} from 'studies/StudyConstants';
import {CreateExperimentsResponse} from 'studies/StudyDAO';
import {
  ClinicalBasedStudyExecutionOverviewQuery,
  ClinicalBasedStudyExecutionOverviewQueryVariables,
} from 'studies/typed/clinical/__generated/ClinicalBasedStudyExecutionOverviewQuery';
import {CLINICAL_BASED_STUDY_EXECUTION_OVERVIEW_QUERY} from 'studies/typed/clinical/ClinicalStudyExecutionOverview';
import {
  ProcessDevelopmentStudyExecutionProgressQuery,
  ProcessDevelopmentStudyExecutionProgressQueryVariables,
} from 'studies/typed/process-development/__generated/ProcessDevelopmentStudyExecutionProgressQuery';
import {PROCESS_DEVELOPMENT_STUDY_EXECUTION_PROGRESS_QUERY} from 'studies/typed/process-development/useProcessDevelopmentStudyProgress';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {BasePendingTask} from 'util/resume-persisted-tasks/PersistedTaskTypes';
import * as TaskRunner from 'util/TaskRunner';

export interface CreateExperimentTaskRequestMetadataCreate {
  name: string;
  studyId: string;
  studyType: StudyType;
}

export interface CreateExperimentTaskRequestMetadata {
  creates: Array<CreateExperimentTaskRequestMetadataCreate>;
}

export type ExperimentCreatePendingTask = BasePendingTask<
  'EXPERIMENT',
  CreateExperimentTaskRequestMetadata,
  CreateExperimentsResponse
>;

export function getExperimentDescriptor(
  creates: Array<CreateExperimentTaskRequestMetadataCreate>
): JSX.Element {
  assert(creates.length > 0, 'Expected at least one create');
  assertAndContinue(
    new Set(creates.map((create) => create.studyType)).size === 1,
    'Expected all creates to have the same studyType'
  );

  if (creates.length > 1) {
    return (
      <>{`${creates.length} ${getProcedureRunUIStringForStudyType(creates[0].studyType, {plural: true})}`}</>
    );
  } else if (creates[0].name) {
    return (
      <>
        {getProcedureRunUIStringForStudyType(creates[0].studyType)} <strong>{creates[0].name}</strong>
      </>
    );
  } else {
    return <>{getProcedureRunUIStringForStudyType(creates[0].studyType)}</>;
  }
}

export function showExperimentCreationTrayTask(
  taskId: string,
  statusUrl: string,
  taskMeta: TaskRunner.TaskMeta,
  creates: Array<CreateExperimentTaskRequestMetadataCreate>,
  callbacks: CreationTrayCallbacks<CreateExperimentsResponse>,
  timingMeta?: ExecutionPlan,
  persistedPendingTask?: ExperimentCreatePendingTask
): void {
  const studyType = creates[0].studyType;
  showCreationTrayTask<'EXPERIMENT', CreateExperimentTaskRequestMetadata, CreateExperimentsResponse>(
    taskId,
    statusUrl,
    taskMeta,
    {
      creation: getExperimentDescriptor(creates),
      creationErrorHeader: `Creating ${getProcedureRunUIStringForStudyType(studyType)} failed.`,
      taskStatusErrorHeader: `Failed to fetch ${getProcedureRunUIStringForStudyType(studyType)} creation progress.`,
    },
    callbacks,
    timingMeta,
    persistedPendingTask
  );
}

export function getExperimentUnknownErrorAction(studyId: string, studyType: StudyType): OnUnknownErrorAction {
  return {
    text: 'View study',
    description: (
      <>
        Click <i>View study</i> to manually verify if the {getProcedureRunUIStringForStudyType(studyType)} was
        created. If not, consider <i>retrying.</i>
      </>
    ),
    onClick: () => {
      navigateToStudyOverviewTab(studyId);
    },
  };
}

export async function onSuccessAfterExperimentCreate(
  studyId: string,
  response: CreateExperimentsResponse,
  studyType: StudyType
): Promise<void> {
  if (studyType === StudyType.CLINICAL || studyType === StudyType.PRECLINICAL) {
    await refreshClinicalBasedStudyOverview(studyId);
  } else {
    await refreshStudyOverview(studyId);
  }
  const folderIds = new Set<string>(response.creates.map((create) => create.folderId));
  folderIds.forEach((folderId) => {
    refreshStudyFolder(folderId);
  });
}

export function onOpenAfterExperimentCreate(studyId: string): void {
  navigateToStudyOverviewTab(studyId);
}

// Refresh study overview by re-running relevant query
export async function refreshStudyOverview(studyId: string): Promise<void> {
  await doQuerySafe<
    ProcessDevelopmentStudyExecutionProgressQuery,
    ProcessDevelopmentStudyExecutionProgressQueryVariables
  >(
    {query: PROCESS_DEVELOPMENT_STUDY_EXECUTION_PROGRESS_QUERY},
    {
      fetchPolicy: 'network-only',
      variables: {studyId},
    }
  );
}

// Refresh study overview by re-running relevant query
export async function refreshClinicalBasedStudyOverview(studyId: string): Promise<void> {
  await doQuerySafe<
    ClinicalBasedStudyExecutionOverviewQuery,
    ClinicalBasedStudyExecutionOverviewQueryVariables
  >(
    {query: CLINICAL_BASED_STUDY_EXECUTION_OVERVIEW_QUERY},
    {
      fetchPolicy: 'network-only',
      variables: {studyId, numActiveProcedureRuns: ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE + 1},
    }
  );
}
